import { useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileUp, Loader2, Sparkles, Vault } from "lucide-react";

const STATUS_OPTIONS = ["Active", "Matured", "Closed", "Auto-Renewed", "Broken"];
const PAYOUT_OPTIONS = ["Cumulative", "Monthly", "Quarterly", "Half-Yearly", "Yearly", "On Maturity"];

export function FDImporter({ onPosted }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [rows, setRows] = useState(null);
  const [replace, setReplace] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setRows(null);
    setPreviewing(false);
    setParsing(false);
    setPosting(false);
    setReplace(false);
  };

  const previewFile = async () => {
    if (!file) return toast.error("Choose an FD summary file first");
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/fixed-deposits/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not read FD file");
    } finally {
      setPreviewing(false);
    }
  };

  const parse = async () => {
    if (!file) return toast.error("Choose an FD summary file first");
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/fixed-deposits/parse-async", fd);
      let job = data;
      const started = Date.now();
      while (job.status === "processing" && Date.now() - started < 1800000) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await api.get(`/fixed-deposits/parse-jobs/${data.job_id}`);
        job = { ...res.data, job_id: data.job_id };
      }
      if (job.status === "error") throw new Error(job.error || "Could not parse FD file");
      if (job.status !== "done") throw new Error("Parsing timed out — please try again");
      const list = (job.fds || []).map((r, idx) => ({ ...r, _uid: `fd-${idx}-${r.fd_number || r.bank}` }));
      if (!list.length) toast.error("No FDs found in this file");
      else toast.success(`Parsed ${list.length} fixed deposits · review before saving`);
      setRows(list);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not parse FD file");
    } finally {
      setParsing(false);
    }
  };

  const setRow = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const post = async () => {
    setPosting(true);
    try {
      const { data } = await api.post("/fixed-deposits/post", { fds: rows, replace });
      const dup = data.skipped_duplicates || 0;
      toast.success(
        `Saved ${data.created} FDs${data.replaced ? " · previous FDs replaced" : ""}${dup ? ` · ${dup} duplicate${dup > 1 ? "s" : ""} skipped` : ""}`
      );
      setOpen(false);
      reset();
      onPosted && onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save FDs");
    } finally {
      setPosting(false);
    }
  };

  const totals = rows
    ? rows.reduce(
        (a, r) => ({
          principal: a.principal + (Number(r.principal) || 0),
          value: a.value + (Number(r.current_value) || Number(r.principal) || 0),
        }),
        { principal: 0, value: 0 }
      )
    : { principal: 0, value: 0 };
  const currency = rows?.[0]?.currency || "INR";

  let stage = "pick";
  if (rows) stage = "review";
  else if (preview) stage = "preview";

  return (
    <Card className="p-5" data-testid="fd-importer">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center shrink-0">
            <Vault className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              Upload FD Summary
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-brand2 border border-brand2/40 rounded-full px-2 py-0.5">
                <Sparkles className="h-3 w-3" /> AI extraction
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Upload the FD summary downloaded from your banking portal (PDF or XLSX). AI reads every deposit — review before saving.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="open-fd-import-btn" className="gap-2 shrink-0">
          <FileUp className="h-4 w-4" /> Upload FD Summary
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Import Fixed Deposit Summary</DialogTitle>
            <DialogDescription>
              {stage === "pick" && "Upload a PDF or XLSX FD summary from your banking portal."}
              {stage === "preview" && "Confirm the file preview looks right before AI extraction."}
              {stage === "review" && "Review each FD row, edit if needed, then save."}
            </DialogDescription>
          </DialogHeader>

          {stage === "pick" && (
            <div className="py-2">
              <Label className="text-xs mb-1.5 block">FD summary file (.pdf, .xlsx, .xls, .csv)</Label>
              <Input
                type="file"
                accept=".pdf,.xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="fd-file-input"
              />
              <p className="text-xs text-muted-foreground mt-2">Max 25MB. AI understands most bank FD summary layouts.</p>
            </div>
          )}

          {stage === "preview" && (
            <div className="py-2 space-y-3" data-testid="fd-preview-panel">
              {preview.file_type === "pdf" ? (
                <p className="text-sm">
                  <span className="font-medium">{file?.name}</span> is a PDF ({preview.pages} page{preview.pages === 1 ? "" : "s"}). AI will read every FD it can find.
                </p>
              ) : (
                <p className="text-sm">
                  <span className="font-medium">{file?.name}</span> · <span className="text-brand">{preview.will_import}</span> FDs detected across {preview.total_rows} rows.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Confirm to run AI extraction — you&apos;ll review each FD before it&apos;s saved to the ledger.
              </p>
            </div>
          )}

          {stage === "review" && (
            <div className="py-1">
              <div className="flex flex-wrap items-center gap-3 mb-3 text-xs">
                <span className="text-muted-foreground">{rows.length} FDs ·</span>
                <span>Principal: <span className="font-mono">{formatMoney(totals.principal, currency)}</span></span>
                <span>Maturity Value: <span className="font-mono">{formatMoney(totals.value, currency)}</span></span>
                <div className="ml-auto flex items-center gap-2">
                  <Checkbox id="fd-replace" checked={replace} onCheckedChange={setReplace} data-testid="fd-replace-checkbox" />
                  <label htmlFor="fd-replace" className="text-xs text-muted-foreground cursor-pointer">
                    Replace all existing FDs
                  </label>
                </div>
              </div>
              <div className="max-h-[56vh] overflow-auto rounded-md border border-border" data-testid="fd-review-table">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-card/95 backdrop-blur z-10">
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="text-left font-medium px-2 py-2">FD No.</th>
                      <th className="text-left font-medium px-2 py-2">Bank</th>
                      <th className="text-right font-medium px-2 py-2">Principal</th>
                      <th className="text-right font-medium px-2 py-2">Rate %</th>
                      <th className="text-left font-medium px-2 py-2">Start</th>
                      <th className="text-left font-medium px-2 py-2">Maturity</th>
                      <th className="text-left font-medium px-2 py-2">Payout</th>
                      <th className="text-right font-medium px-2 py-2">Maturity Val.</th>
                      <th className="text-left font-medium px-2 py-2">Status</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r._uid || i} className="border-t border-border hover:bg-muted/40" data-testid={`fd-row-${i}`}>
                        <td className="px-2 py-1.5">
                          <input value={r.fd_number || ""} onChange={(e) => setRow(i, { fd_number: e.target.value })}
                            className="w-24 bg-background border border-input rounded px-1.5 py-1 font-mono text-[11px]" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input value={r.bank || ""} onChange={(e) => setRow(i, { bank: e.target.value })}
                            className="w-28 bg-background border border-input rounded px-1.5 py-1" />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" step="0.01" value={r.principal || 0}
                            onChange={(e) => setRow(i, { principal: Number(e.target.value) })}
                            className="w-24 bg-background border border-input rounded px-1.5 py-1 text-right font-mono" />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" step="0.01" value={r.interest_rate || 0}
                            onChange={(e) => setRow(i, { interest_rate: Number(e.target.value) })}
                            className="w-16 bg-background border border-input rounded px-1.5 py-1 text-right font-mono" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="date" value={r.start_date || ""}
                            onChange={(e) => setRow(i, { start_date: e.target.value })}
                            className="w-32 bg-background border border-input rounded px-1.5 py-1 font-mono text-[11px]" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="date" value={r.maturity_date || ""}
                            onChange={(e) => setRow(i, { maturity_date: e.target.value })}
                            className="w-32 bg-background border border-input rounded px-1.5 py-1 font-mono text-[11px]" />
                        </td>
                        <td className="px-2 py-1.5">
                          <select value={r.payout_type || "Cumulative"}
                            onChange={(e) => setRow(i, { payout_type: e.target.value })}
                            className="w-24 bg-background border border-input rounded px-1.5 py-1">
                            {PAYOUT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" step="0.01" value={r.current_value || 0}
                            onChange={(e) => setRow(i, { current_value: Number(e.target.value) })}
                            className="w-24 bg-background border border-input rounded px-1.5 py-1 text-right font-mono" />
                        </td>
                        <td className="px-2 py-1.5">
                          <select value={r.status || "Active"}
                            onChange={(e) => setRow(i, { status: e.target.value })}
                            className="w-24 bg-background border border-input rounded px-1.5 py-1">
                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="px-1 py-1.5 text-right">
                          <Button size="sm" variant="ghost" onClick={() => removeRow(i)} data-testid={`fd-remove-${i}`}
                            className="h-6 w-6 p-0 text-danger hover:text-danger">×</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter>
            {stage === "pick" && (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
                <Button onClick={previewFile} disabled={previewing || !file} data-testid="preview-fd-btn" className="gap-2">
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                  {previewing ? "Reading file…" : "Preview Import"}
                </Button>
              </>
            )}
            {stage === "preview" && (
              <>
                <Button variant="outline" onClick={() => setPreview(null)} disabled={parsing}>Back</Button>
                <Button onClick={parse} disabled={parsing} data-testid="confirm-fd-parse-btn" className="gap-2">
                  {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {parsing ? "AI extracting FDs…" : "Confirm & AI-Extract"}
                </Button>
              </>
            )}
            {stage === "review" && (
              <>
                <Button variant="outline" onClick={() => setRows(null)} disabled={posting}>Back</Button>
                <Button onClick={post} disabled={posting || !rows.length} data-testid="save-fds-btn" className="gap-2">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Vault className="h-4 w-4" />}
                  Save {rows.length} FDs
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
